import { useCallback, useState } from "react";

import { invalidateCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentLimitsService } from "../services/paymentLimits.service";
import type { ClearAmountLimitScope, UpsertAmountLimitPayload } from "../types/paymentLimits.types";

export interface UseLimitMutationsResult {
  /** True while a write is in flight — the owning dialog must block closing and double-submits. */
  isSubmitting: boolean;
  /** Normalized user-facing message of the last failed mutation; cleared on every new attempt. */
  error: string | null;
  clearError: () => void;
  /**
   * `PUT /internal/payments/limits/:rail`. Resolves null on success, or the normalized error message —
   * returned (not only stored in `error`) so a caller can toast it right after the await.
   */
  setLimit: (rail: string, payload: UpsertAmountLimitPayload) => Promise<string | null>;
  /** `DELETE /internal/payments/limits/:rail`. A 404 counts as done — the row was already gone. */
  clearLimit: (rail: string, scope: ClearAmountLimitScope) => Promise<string | null>;
}

/**
 * The limits write path. Owns the HTTP calls, the in-flight flag and the error normalization so the dialogs
 * stay presentational. Every successful write also drops the shared corridors memo, so the payin forms and
 * the dispersion wizard pick up the new bounds without a reload.
 */
export function useLimitMutations(): UseLimitMutationsResult {
  const { t } = useTranslations("paymentLimits");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalize = useCallback(
    (caught: unknown): string =>
      HttpApiService.isAccessDeniedError(caught)
        ? t("error_no_write_permission")
        : HttpApiService.getErrorMessage(caught) || t("error_save"),
    [t],
  );

  const setLimit = useCallback(
    async (rail: string, payload: UpsertAmountLimitPayload): Promise<string | null> => {
      setIsSubmitting(true);
      setError(null);
      try {
        await PaymentLimitsService.upsert(rail, payload);
        invalidateCorridors();
        return null;
      } catch (caught: unknown) {
        const message = normalize(caught);
        setError(message);
        return message;
      } finally {
        setIsSubmitting(false);
      }
    },
    [normalize],
  );

  const clearLimit = useCallback(
    async (rail: string, scope: ClearAmountLimitScope): Promise<string | null> => {
      setIsSubmitting(true);
      setError(null);
      try {
        await PaymentLimitsService.clear(rail, scope);
        invalidateCorridors();
        return null;
      } catch (caught: unknown) {
        if (HttpApiService.isNotFoundError(caught)) {
          invalidateCorridors();
          return null;
        }
        const message = normalize(caught);
        setError(message);
        return message;
      } finally {
        setIsSubmitting(false);
      }
    },
    [normalize],
  );

  return {
    isSubmitting,
    error,
    clearError: useCallback(() => setError(null), []),
    setLimit,
    clearLimit,
  };
}
